import { useClient } from '@/composables/useClient'
import { HttpError } from '@/queries/auth-error'
import { entityKeys } from '@/queries/entity-keys'
import { mutationOptions } from '@/queries/mutation-options'
import {
  type AddDogToSubscriptionInput,
  type RecalculateDogMealPlanSubscriptionInputSchema,
  type UpdateDogBody,
} from '@lyka-pet-food/contracts'
import { queryOptions } from '@tanstack/vue-query'
import { computed, type MaybeRef, toValue } from 'vue'
import { type z } from 'zod'

type RecalculateDogMealPlanSubscriptionInput = z.infer<typeof RecalculateDogMealPlanSubscriptionInputSchema>
type AddDogToSubscriptionRequest = Omit<AddDogToSubscriptionInput, 'userId' | 'subscriptionId' | 'workflowId'>

const animalKeys = {
  list: () => [...entityKeys.animal()] as const,
  meta: () => [...entityKeys.animalMeta()] as const,
  recalculateMealPlan: (input: RecalculateDogMealPlanSubscriptionInput | null) =>
    [...entityKeys.animal(), 'recalculate-meal-plan', input] as const,
  previewNewSubscription: (input: AddDogToSubscriptionRequest | null) =>
    [...entityKeys.animal(), 'preview-new-subscription', input] as const,
}

export const animalQueries = {
  list: () =>
    queryOptions({
      queryKey: animalKeys.list(),
      queryFn: async () => {
        const client = await useClient()
        const { status, body } = await client.animals.getDogs()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch dogs')
      },
    }),

  meta: () =>
    queryOptions({
      staleTime: Infinity,
      queryKey: animalKeys.meta(),
      queryFn: async () => {
        const client = await useClient()
        const { status, body } = await client.animals.getDogsData()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch dog metadata')
      },
    }),

  recalculateMealPlan: (input: MaybeRef<RecalculateDogMealPlanSubscriptionInput | null>) =>
    queryOptions({
      queryKey: computed(() => animalKeys.recalculateMealPlan(toValue(input))),
      enabled: computed(() => toValue(input) !== null),
      queryFn: async () => {
        const value = toValue(input)
        if (value === null) throw new Error('recalculateMealPlan called with null input')

        const client = await useClient()
        const { status, body } = await client.animals.recalculateDogMealPlanSubscription({ body: value })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch the recommendation for the new dog')
      },
    }),

  previewNewSubscription: (input: MaybeRef<AddDogToSubscriptionRequest | null>) =>
    queryOptions({
      queryKey: computed(() => animalKeys.previewNewSubscription(toValue(input))),
      enabled: computed(() => toValue(input) !== null),
      queryFn: async () => {
        const value = toValue(input)
        if (value === null) throw new Error('previewNewSubscription called with null input')

        const client = await useClient()
        const { status, body } = await client.animals.previewNewSubscription({ body: value })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch the preview of the new subscription')
      },
    }),
}

export const animalMutations = {
  update: () =>
    mutationOptions({
      mutationFn: async ({ id, dog }: { id: string; dog: UpdateDogBody }) => {
        const client = await useClient()
        const { status, body } = await client.animals.updateDog({
          params: { id },
          body: { dog },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update dog')
      },
      invalidation: {
        awaited: [{ entity: 'animal' }],
        background: [{ entity: 'subscription' }, { entity: 'order' }, { entity: 'product' }],
      },
    }),

  previewNewSubscription: () =>
    mutationOptions({
      mutationFn: async (input: AddDogToSubscriptionRequest) => {
        const client = await useClient()
        const { status, body } = await client.animals.previewNewSubscription({ body: input })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch the preview of the new subscription')
      },
      invalidation: 'skip',
    }),

  addToSubscription: () =>
    mutationOptions({
      mutationFn: async (input: AddDogToSubscriptionRequest): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.animals.addDogToSubscription({ body: input })

        if (status === 201) return

        throw new HttpError(status, body, 'Failed to add dog to subscription')
      },
      invalidation: {
        awaited: [{ entity: 'animal' }],
        background: [{ entity: 'subscription' }, { entity: 'order' }],
      },
    }),

  uploadPhoto: () =>
    mutationOptions({
      mutationFn: async ({ animalId, file }: { animalId: string; file: File }): Promise<void> => {
        const client = await useClient()

        const presignedResult = await client.animals.getPresignedPost({
          params: { id: animalId },
          body: { extension: getExtension(file) },
        })
        if (presignedResult.status !== 200) {
          throw new HttpError(presignedResult.status, presignedResult.body, 'Failed to get upload URL')
        }

        const { url, fields, key } = presignedResult.body
        const formData = new FormData()
        Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
        if (!fields['key']) formData.append('key', key)
        formData.append('Content-Type', file.type || 'image/jpeg')
        formData.append('file', file)

        const s3Response = await fetch(url, { method: 'POST', body: formData })
        if (!s3Response.ok) {
          throw new HttpError(s3Response.status, null, 'Failed to upload photo')
        }

        const saveResult = await client.animals.createAnimalImage({
          params: { animalId },
          body: { url: key },
        })
        if (saveResult.status !== 204) {
          throw new HttpError(saveResult.status, saveResult.body, 'Failed to save photo')
        }
      },
      invalidation: { awaited: [{ entity: 'animal' }] },
    }),

  removePhoto: () =>
    mutationOptions({
      mutationFn: async ({ animalId }: { animalId: string }): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.animals.removeAnimalImage({ params: { animalId } })

        if (status === 204) return

        throw new HttpError(status, body, 'Failed to remove photo')
      },
      invalidation: { awaited: [{ entity: 'animal' }] },
    }),
}

const MIME_TO_EXTENSION: Record<string, 'jpg' | 'jpeg' | 'png' | 'webp'> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

function getExtension(file: File): 'jpg' | 'jpeg' | 'png' | 'webp' | 'heic' | 'heif' {
  return MIME_TO_EXTENSION[file.type] ?? 'jpg'
}
